"use client"

import { useState } from "react"
import { Plus, X, Trash2, Pencil, Check } from "@/lib/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { useBlacklistStore, type BlacklistGroup } from "@/lib/blacklist-store"
import { useTranslations } from "next-intl"

const splitTags = (text: string) =>
  text
    .split(/[,\n]+|\s+/g)
    .map((t) => t.trim())
    .filter(Boolean)

/** The viewer's own named blacklist groups (Settings > Blacklist > Groups). Their tags are the viewer's own, so they are shown. */
export function BlacklistGroupsTab() {
  const tt = useTranslations("blacklistManager")
  const groups = useBlacklistStore((s) => s.groups)
  const createGroup = useBlacklistStore((s) => s.createGroup)
  const [name, setName] = useState("")
  const [tags, setTags] = useState("")

  const handleCreate = () => {
    if (!name.trim()) return
    createGroup(name, splitTags(tags))
    setName("")
    setTags("")
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        <Input
          id="new-blacklist-group-name"
          placeholder={tt("groupNamePlaceholder")}
          value={name}
          maxLength={40}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCreate()}
        />
        <Input
          id="new-blacklist-group-tags"
          placeholder={tt("groupTagsPlaceholder")}
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleCreate()}
        />
        <Button onClick={handleCreate} size="sm" className="w-full" disabled={!name.trim()}>
          <Plus className="h-4 w-4 mr-2" />
          {tt("createGroup")}
        </Button>
      </div>

      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
        {groups.length > 0 ? (
          groups.map((group) => <GroupRow key={group.id} group={group} />)
        ) : (
          <p className="text-sm text-muted-foreground italic">{tt("noGroups")}</p>
        )}
      </div>
    </div>
  )
}

function GroupRow({ group }: { group: BlacklistGroup }) {
  const tt = useTranslations("blacklistManager")
  const renameGroup = useBlacklistStore((s) => s.renameGroup)
  const deleteGroup = useBlacklistStore((s) => s.deleteGroup)
  const toggleGroup = useBlacklistStore((s) => s.toggleGroup)
  const addTagToGroup = useBlacklistStore((s) => s.addTagToGroup)
  const removeTagFromGroup = useBlacklistStore((s) => s.removeTagFromGroup)
  const [editing, setEditing] = useState(false)
  const [draftName, setDraftName] = useState(group.name)
  const [tagInput, setTagInput] = useState("")

  const saveName = () => {
    renameGroup(group.id, draftName)
    setEditing(false)
  }
  const addTags = () => {
    for (const tag of splitTags(tagInput)) addTagToGroup(group.id, tag)
    setTagInput("")
  }

  return (
    <div className="rounded-md border p-2 space-y-2">
      <div className="flex items-center gap-2">
        <Switch
          id={`blacklist-group-${group.id}`}
          checked={group.enabled}
          onCheckedChange={() => toggleGroup(group.id)}
          aria-label={tt("groupOnOff", { name: group.name })}
        />
        {editing ? (
          <Input
            id={`blacklist-group-name-${group.id}`}
            className="h-7 text-sm"
            value={draftName}
            maxLength={40}
            autoFocus
            onChange={(e) => setDraftName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") saveName()
              if (e.key === "Escape") setEditing(false)
            }}
          />
        ) : (
          <span className="text-sm font-medium truncate flex-1">{group.name}</span>
        )}
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 shrink-0"
          aria-label={editing ? tt("saveName") : tt("renameGroup")}
          onClick={() => (editing ? saveName() : (setDraftName(group.name), setEditing(true)))}
        >
          {editing ? <Check className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
        </Button>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" aria-label={tt("deleteGroup")} onClick={() => deleteGroup(group.id)}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {group.tags.length > 0 ? (
          group.tags.map((tag) => (
            <Badge key={tag} variant="destructive" className="gap-1 pl-2 pr-1">
              {tag}
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 hover:bg-transparent"
                aria-label={tt("removeTag", { tag })}
                onClick={() => removeTagFromGroup(group.id, tag)}
              >
                <X className="h-3 w-3" />
              </Button>
            </Badge>
          ))
        ) : (
          <p className="text-xs text-muted-foreground italic">{tt("groupEmpty")}</p>
        )}
      </div>

      <div className="flex gap-2">
        <Input
          id={`blacklist-group-add-${group.id}`}
          className="h-7 text-sm"
          placeholder={tt("addTagsToGroup")}
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addTags()}
        />
        <Button onClick={addTags} size="sm" className="h-7 px-2" aria-label={tt("addTagsToGroup")}>
          <Plus className="h-3 w-3" />
        </Button>
      </div>
    </div>
  )
}
